import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { GetPlaylistParams, Playlist } from '@jwp/ott-common/types/playlist';
import ContentApiService from '@jwp/ott-common/src/services/ContentApiService';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { generatePlaylistPlaceholder } from '@jwp/ott-common/src/utils/collection';
import { isScheduledOrLiveMedia } from '@jwp/ott-common/src/utils/liveEvent';
import { isTruthyCustomParamValue } from '@jwp/ott-common/src/utils/common';
import type { ApiError } from '@jwp/ott-common/src/utils/api';
import type { AppMenuType, Config } from '@jwp/ott-common/types/config';
import { APP_CONFIG_ITEM_TYPE } from '@jwp/ott-common/src/constants';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { useTranslation } from 'react-i18next';

const placeholderData = generatePlaylistPlaceholder(30);

export const getPlaylistQueryOptions = ({
  type,
  contentId,
  enabled,
  usePlaceholderData,
  params = {},
  language,
  config,
}: {
  type: AppMenuType;
  contentId: string | undefined;
  enabled: boolean;
  usePlaceholderData?: boolean;
  params?: GetPlaylistParams;
  language: string;
  config: Config;
}): UseQueryOptions<Playlist | undefined, ApiError> => {
  const contentApiService = getModule(ContentApiService);

  return {
    enabled: !!contentId && enabled,
    queryKey: ['playlist', type, contentId, params, language],
    queryFn: async () => contentApiService.getPlaylistById({ type, language, config, params, id: contentId! }),
    placeholderData: contentId && usePlaceholderData ? placeholderData : undefined,
    refetchInterval: (query) => {
      if (!query.state.data) return false;

      const autoRefetch = isTruthyCustomParamValue(query.state.data.refetch) || query.state.data.playlist.some(isScheduledOrLiveMedia);

      return autoRefetch ? 1000 * 30 : false;
    },
    retry: false,
  };
};

export default function usePlaylist(
  contentId?: string,
  params: GetPlaylistParams = {},
  enabled: boolean = true,
  usePlaceholderData: boolean = true,
  type: AppMenuType = APP_CONFIG_ITEM_TYPE.playlist,
) {
  // Determine currently selected language
  const { i18n } = useTranslation();
  const language = i18n.language;

  const config = useConfigStore((state) => state.config);

  const queryOptions = getPlaylistQueryOptions({
    type,
    contentId,
    params,
    enabled,
    usePlaceholderData,
    language,
    config,
  });

  return useQuery(queryOptions);
}
